import {
  ComputedRef,
  MaybeRefOrGetter,
  Ref,
  computed,
  ref,
  toValue,
  watch,
} from "vue";
import * as lodash from "lodash-es";
import { watchPausable } from "@vueuse/core";

export function useResetPage<T extends { page: Ref<number> }>(
  page: Ref<number>,
  queryKey: readonly [string, T],
  ignoredKeys: readonly (keyof T)[] = [],
  enabled?: Ref<boolean>,
): void {
  const { pause, resume } = watchPausable(
    () => lodash.omit(queryKey[1], ["page", ...ignoredKeys]),
    () => {
      page.value = 1;
    },
    { deep: true },
  );
  watch(
    () => enabled?.value ?? true,
    (enabled) => {
      if (enabled) {
        resume();
      } else {
        pause();
      }
    },
    { immediate: true },
  );
}

// Marketing endpoints (MarketingPagination) skip COUNT(*) and return count=null on page 2+.
export function useCachedPageCount({
  count,
  next,
  page,
  pageSize,
}: {
  count: MaybeRefOrGetter<number | null | undefined>;
  next: MaybeRefOrGetter<string | null | undefined>;
  page: MaybeRefOrGetter<number>;
  pageSize: MaybeRefOrGetter<number>;
}): ComputedRef<number> {
  const cachedCount = ref<number>();
  watch(
    () => toValue(count),
    (value) => {
      if (value !== null && value !== undefined) {
        cachedCount.value = value;
      }
    },
    { immediate: true },
  );
  return computed(() => {
    if (cachedCount.value !== undefined) {
      return Math.ceil(cachedCount.value / toValue(pageSize));
    }
    // Reload or shared link on page 2+: the total is genuinely unknown, so report it
    // as such. TablePaginationControls hides a non-finite count and keeps next enabled.
    return toValue(next) ? Infinity : toValue(page);
  });
}

export function circumventAdBlocker(s: string): string {
  const replacements = {
    google_ads: "gads",
    google_analytics: "ganalytics",
  };
  return Object.entries(replacements).reduce(
    (s, [from, to]) =>
      s.replace(from, to).replace(from.toUpperCase(), to.toUpperCase()),
    s,
  );
}

export interface PaginatedResponse<T> {
  next: string | null;
  previous: string | null;
  results: T[];
  count?: number;
}
